import React, { useEffect, useRef } from 'react'
import { Modal, ModalBody, ModalFooter, Button } from 'reactstrap'
import { useSelector } from 'react-redux'
import intl from 'react-intl-universal'
import _ from 'lodash'

import { EllipsisReduxStore, ANSGallery, ANSImage } from '~/types'
import { isAns } from '~/modules/services/thirdPartyIntegration'
import useStory from '~/reactapp/shared/use-story'
// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./AnglerfishModal.styles.scss')

interface CommonProps {
  visible: boolean
  onClose: () => void
}

interface SingleProps<SelectedItemsType> {
  selectType: 'single'
  onSelect: (items: SelectedItemsType | SelectedItemsType[]) => void
}

interface MultipleProps<SelectedItemsType> {
  selectType: 'multiple'
  onSelect: (items: SelectedItemsType[]) => void
}

export type ImageProps = CommonProps &
  (SingleProps<ANSImage> | MultipleProps<ANSImage>) & {
    contentType: 'image'
  }

export type GalleryProps = CommonProps &
  (SingleProps<ANSGallery> | MultipleProps<ANSGallery>) & {
    contentType: 'gallery'
  }

export default function AnglerfishModal(props: ImageProps | GalleryProps) {
  const { onSelect, contentType, visible, selectType } = props

  const iframeRef = useRef<HTMLIFrameElement>(null)

  const webskedTasks = useSelector(
    (state: EllipsisReduxStore) => state.websked.tasks
  )
  const story = useStory()

  const cleanPhotoCenterSessionStorage = () => {
    // remove session storage created from photo center modal
    // required to open selector on associated lightbox (if set)
    // more info at: https://github.com/WPMedia/arc-photo-ui/tree/develop/packages/photos#parent-application-your-application-to-remove-async-session-storage
    if (iframeRef?.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { message: 'cancelSelection' },
        window.location.origin
      )
    }
  }

  const handleSelection = () => {
    const postArg =
      contentType === 'gallery'
        ? 'returnSelectedANS'
        : { message: 'returnSelectedANS' }

    if (iframeRef?.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        postArg,
        window.location.origin
      )
    }

    if (contentType !== 'gallery') cleanPhotoCenterSessionStorage()
  }

  const handleCancel = () => {
    if (contentType !== 'gallery') {
      cleanPhotoCenterSessionStorage()
    }
    setTimeout(() => {
      props.onClose()
    }, 100)
  }

  useEffect(() => {
    if (!visible) {
      return
    }

    function sendInitData() {
      // Only send AF-related task IDs.
      const associatedTaskIds = webskedTasks
        .filter((task) => task.authoringApp === 'Anglerfish')
        .map((task) => task.id)

      let primaryWebsite: null | string = null
      primaryWebsite = _.get(story, 'canonical_website', '')

      if (iframeRef?.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          {
            message: 'selectorInitData',
            data: {
              associatedTasks: associatedTaskIds,
              primaryWebsite,
            },
          },
          window.location.origin
        )
      }
    }

    function handleMessage(event: MessageEvent) {
      const { data } = event
      if (data.message === 'sendSelectorInitData') {
        sendInitData()
      } else if (isAns(data)) {
        // Inspecting the data structure seems to be the only way to validate the AF message
        onSelect(data)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [onSelect, visible, story, webskedTasks])

  const imageSelectorUrl =
    contentType === 'gallery'
      ? `/photo/galleries/selector/${
          selectType !== 'single' ? '?select=multiple' : ''
        }`
      : `/photo/v2?selector=true&multiple=${selectType === 'multiple'}`

  return (
    <Modal
      wrapClassName="bootstrap4"
      className={styles.root}
      isOpen={props.visible}
      toggle={handleCancel}
    >
      <ModalBody>
        {story && (
          <iframe
            id="photosSelectorV2"
            ref={iframeRef}
            title="Anglerfish Selector"
            height="100%"
            width="100%"
            src={imageSelectorUrl}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button onClick={handleCancel}>
          {intl.get('addItemPlugin.BaseModal.cancel')}
        </Button>
        <Button onClick={handleSelection} color="success">
          {intl.get('addItemPlugin.BaseModal.select')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
